import { useEffect } from 'react';

import { useNavigate } from 'react-router';

import {
  Activity,
  BarChart2,
  BarChart3,
  CreditCard,
  Globe,
  Key,
  Layers,
  LayoutDashboard,
  Lightbulb,
  Scan,
  Settings,
  Terminal,
  Users,
  Webhook,
} from 'lucide-react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/lib/shadcn/command';
import { Dialog, DialogContent } from '@/lib/shadcn/dialog';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/routes';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  closeCommandPalette,
  openCommandPalette,
  selectIsCommandPaletteOpen,
  toggleCommandPalette,
} from '@/store/uiSlice';

import styles from './CommandPalette.module.scss';

interface CommandPaletteProps {
  isAdmin?: boolean;
}

export function CommandPalette({ isAdmin = false }: CommandPaletteProps) {
  const open = useAppSelector(selectIsCommandPaletteOpen);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        dispatch(toggleCommandPalette());
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatch]);

  const go = (path: string) => {
    navigate(path);
    dispatch(closeCommandPalette());
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => dispatch(next ? openCommandPalette() : closeCommandPalette())}
    >
      <DialogContent
        className={cn(styles.dialogContent, '!bg-[var(--color-glass-bg)] overflow-hidden p-0')}
      >
        <Command className="!bg-transparent [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          <CommandInput placeholder="Search pages and actions…" />
          <CommandList className="max-h-[420px]">
            <CommandEmpty>No results found.</CommandEmpty>

            <CommandGroup heading="Actions">
              <CommandItem onSelect={() => go(`${ROUTES.apiKeys}?create=1`)}>
                <Key />
                New API Key
              </CommandItem>
              <CommandItem onSelect={() => go(`${ROUTES.webhooks}?create=1`)}>
                <Webhook />
                Add Webhook
              </CommandItem>
              <CommandItem onSelect={() => go(`${ROUTES.allowedHosts}?create=1`)}>
                <Globe />
                Add Allowed Host
              </CommandItem>
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="Navigate">
              <CommandItem onSelect={() => go(ROUTES.dashboard)}>
                <LayoutDashboard />
                Dashboard
              </CommandItem>
              <CommandItem onSelect={() => go(ROUTES.apiKeys)}>
                <Key />
                API Keys
              </CommandItem>
              <CommandItem onSelect={() => go(ROUTES.analytics)}>
                <BarChart3 />
                Analytics
              </CommandItem>
              <CommandItem onSelect={() => go(ROUTES.webhooks)}>
                <Webhook />
                Webhooks
              </CommandItem>
              <CommandItem onSelect={() => go(ROUTES.webhookLogs)}>
                <Webhook />
                Webhook Logs
              </CommandItem>
              <CommandItem onSelect={() => go(ROUTES.activity)}>
                <Activity />
                Activity
              </CommandItem>
              <CommandItem onSelect={() => go(ROUTES.allowedHosts)}>
                <Globe />
                Allowed Hosts
              </CommandItem>
              <CommandItem onSelect={() => go(ROUTES.mcpSessions)}>
                <Terminal />
                MCP Sessions
              </CommandItem>
              <CommandItem onSelect={() => go(ROUTES.modelCards)}>
                <Layers />
                Model Cards
              </CommandItem>
              <CommandItem onSelect={() => go(ROUTES.billing)}>
                <CreditCard />
                Billing
              </CommandItem>
              <CommandItem onSelect={() => go(ROUTES.settings)}>
                <Settings />
                Security Settings
              </CommandItem>
              <CommandItem onSelect={() => go(ROUTES.settingsOrganization)}>
                <Settings />
                Organization Settings
              </CommandItem>
              <CommandItem onSelect={() => go(ROUTES.howItWorks)}>
                <Lightbulb />
                How It Works
              </CommandItem>
            </CommandGroup>

            {isAdmin && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Admin">
                  <CommandItem onSelect={() => go(ROUTES.adminUsers)}>
                    <Users />
                    Users
                  </CommandItem>
                  <CommandItem onSelect={() => go(ROUTES.adminApiKeys)}>
                    <Key />
                    API Keys
                  </CommandItem>
                  <CommandItem onSelect={() => go(ROUTES.adminApiUsage)}>
                    <BarChart2 />
                    API Usage
                  </CommandItem>
                  <CommandItem onSelect={() => go(ROUTES.adminDetections)}>
                    <Scan />
                    Detections
                  </CommandItem>
                  <CommandItem onSelect={() => go(ROUTES.adminAllowedHosts)}>
                    <Globe />
                    Allowed Hosts
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
